import React from 'react';
import styled from 'styled-components';

// Styled Components
const CartContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 12px;
  padding: 24px;
  color: white;
  min-height: 400px;
  max-height: 80vh;
  overflow-y: auto;
`;

const CartHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const CartTitle = styled.h2`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: bold;
`;

const CartSubtitle = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 16px;
`;

const CartItems = styled.div`
  margin-bottom: 24px;
`;

const CartItem = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 2px solid #374151;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #D4AF37;
    transform: translateY(-2px);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h3`
  color: #D4AF37;
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: bold;
`;

const ItemRestaurant = styled.p`
  color: #9ca3af;
  margin: 0;
  font-size: 14px;
`;

const ItemDescription = styled.p`
  color: #d1d5db;
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.4;
`;

const ItemActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemPrice = styled.div`
  color: #22c55e;
  font-size: 20px;
  font-weight: bold;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #D4AF37;
  color: #0a0a0a;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #fbbf24;
    transform: scale(1.1);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityDisplay = styled.span`
  color: white;
  font-weight: 500;
  font-size: 16px;
  min-width: 30px;
  text-align: center;
`;

const RemoveButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(239, 68, 68, 0.3);
  }
`;

const CartSummary = styled.div`
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 16px;
    border-top: 2px solid #4b5563;
  }
`;

const SummaryLabel = styled.span`
  color: #d1d5db;
  font-size: 16px;
`;

const SummaryValue = styled.span`
  color: ${props => props.$highlight ? '#D4AF37' : '#22c55e'};
  font-size: ${props => props.$highlight ? '24px' : '16px'};
  font-weight: ${props => props.$highlight ? 'bold' : '500'};
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyCartIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyCartText = styled.p`
  color: #9ca3af;
  font-size: 16px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const CloseButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: #9ca3af;
  border: 2px solid #4b5563;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #6b7280;
    color: #d1d5db;
  }
`;

const CheckoutButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #D4AF37 0%, #fbbf24 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(212, 175, 55, 0.3);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ShoppingCart = ({ items, onClose, onCheckout, onRemoveItem, onUpdateQuantity }) => {
  const calculateSubtotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const handleQuantityChange = (itemId, change) => {
    const item = items.find(item => item.id === itemId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + change);
      onUpdateQuantity(itemId, newQuantity);
    }
  };

  if (items.length === 0) {
    return (
      <CartContainer>
        <CartHeader>
          <CartTitle>🛒 Shopping Cart</CartTitle>
          <CartSubtitle>Your cart is empty</CartSubtitle>
        </CartHeader>
        
        <EmptyCart>
          <EmptyCartIcon>🛒</EmptyCartIcon>
          <EmptyCartText>
            Start adding delicious items from our restaurant marketplace!
          </EmptyCartText>
        </EmptyCart>
        
        <ActionButtons>
          <CloseButton onClick={onClose}>
            Continue Shopping
          </CloseButton>
        </ActionButtons>
      </CartContainer>
    );
  }

  return (
    <CartContainer>
      <CartHeader>
        <CartTitle>🛒 Shopping Cart</CartTitle>
        <CartSubtitle>
          {items.length} item{items.length !== 1 ? 's' : ''} • {items.filter((item, index, arr) => arr.findIndex(i => i.vendorId === item.vendorId) === index).length} vendor{items.filter((item, index, arr) => arr.findIndex(i => i.vendorId === item.vendorId) === index).length !== 1 ? 's' : ''}
        </CartSubtitle>
      </CartHeader>

      <CartItems>
        {items.map(item => (
          <CartItem key={item.id}>
            <ItemHeader>
              <ItemInfo>
                <ItemName>{item.name}</ItemName>
                <ItemRestaurant>from {item.vendorName || 'Unknown Vendor'}</ItemRestaurant>
              </ItemInfo>
            </ItemHeader>
            
            <ItemDescription>{item.description}</ItemDescription>
            
            <ItemActions>
              <ItemPrice>{formatPrice(item.price * item.quantity)}</ItemPrice>
              
              <QuantityControl>
                <QuantityButton
                  onClick={() => handleQuantityChange(item.id, -1)}
                  disabled={item.quantity <= 1}
                >
                  -
                </QuantityButton>
                <QuantityDisplay>{item.quantity}</QuantityDisplay>
                <QuantityButton
                  onClick={() => handleQuantityChange(item.id, 1)}
                >
                  +
                </QuantityButton>
              </QuantityControl>
              
              <RemoveButton onClick={() => onRemoveItem(item.id)}>
                Remove
              </RemoveButton>
            </ItemActions>
          </CartItem>
        ))}
      </CartItems>

      <CartSummary>
        <SummaryRow>
          <SummaryLabel>Subtotal ({items.length} items)</SummaryLabel>
          <SummaryValue>{formatPrice(calculateSubtotal())}</SummaryValue>
        </SummaryRow>
        
        <SummaryRow>
          <SummaryLabel>Tax (8%)</SummaryLabel>
          <SummaryValue>{formatPrice(calculateTax())}</SummaryValue>
        </SummaryRow>
        
        <SummaryRow>
          <SummaryLabel>Total</SummaryLabel>
          <SummaryValue $highlight>{formatPrice(calculateTotal())}</SummaryValue>
        </SummaryRow>
      </CartSummary>

      <ActionButtons>
        <CloseButton onClick={onClose}>
          Continue Shopping
        </CloseButton>
        <CheckoutButton onClick={onCheckout}>
          🚀 Proceed to Checkout
        </CheckoutButton>
      </ActionButtons>
    </CartContainer>
  );
};

export default ShoppingCart;
